import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    BadgeCheck,
    Brush,
    Landmark,
    ReceiptText,
    ShieldCheck,
    Wrench
} from 'lucide-react'
import { pad } from '@/lib/format'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { Reveal } from '@/components/motion/reveal'
import { guarantees } from '@/constants/trust'
import { siteConfig } from '@/constants/site'

const icons: LucideIcon[] = [ShieldCheck, Wrench, ReceiptText, BadgeCheck, Landmark, Brush]

export const GuaranteesSection: FC = (): ReactNode => {
    return (
        <section id='guarantees' className='py-24 md:py-32'>
            <Container className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-4'>
                    <div className='lg:sticky lg:top-32'>
                        <SectionHeading
                            layout='stack'
                            eyebrow="Our promises"
                            title="Promises we put in writing."
                            titleClassName='max-w-[12ch]'
                        />

                        <p className='mt-8 inline-flex items-center gap-2 rounded-full border border-ink/12 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-slate'>
                            {siteConfig.license} · {siteConfig.insured}
                        </p>
                    </div>
                </div>

                <Reveal
                    selector=':scope > li'
                    as='ul'
                    className='grid gap-px overflow-hidden rounded-[30px] border border-line bg-line sm:grid-cols-2 lg:col-span-8'
                >
                    {guarantees.map((guarantee, index) => {
                        const Icon = icons[index]

                        return (
                            <li key={guarantee.title} className='group flex flex-col justify-between gap-12 bg-card p-7 transition-colors duration-500 hover:bg-paper sm:p-9'>
                                <div className='flex items-center justify-between'>
                                    <span className='font-mono text-[12px] text-slate'>{pad(index + 1)}</span>

                                    <span className='flex size-11 items-center justify-center rounded-full bg-sand text-ink transition-colors duration-500 group-hover:bg-ember group-hover:text-white'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>
                                </div>

                                <div>
                                    <h3 className='font-heading text-[1.5rem] font-semibold leading-tight tracking-[-0.03em]'>
                                        {guarantee.title}
                                    </h3>

                                    <p className='mt-3 text-[15px] leading-relaxed text-slate'>
                                        {guarantee.body}
                                    </p>
                                </div>
                            </li>
                        )
                    })}
                </Reveal>
            </Container>
        </section>
    )
}
